import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/shared/hooks/useAuth';
import { usePermissionCheck } from '@/shared/hooks/usePermissionCheck';

type PermissionCheck =
  | 'isAdmin'
  | 'isOperador'
  | 'isVisualizador'
  | 'isTenant'
  | 'isStandAlone'
  | 'isPlatformAdmin';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredChecks?: PermissionCheck[];
  fallbackPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredChecks = [],
  fallbackPath = '/login',
}) => {
  const { t } = useTranslation('common');
  const { isAuthenticated, isLoading } = useAuth();
  const permissionCheck = usePermissionCheck();
  const location = useLocation();

  const resolveCheck = (p: PermissionCheck): boolean => {
    const fn = permissionCheck[p] as (() => boolean) | undefined;
    return fn ? fn() : false;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-muted-foreground">{t('feedback.checkingAuth')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={fallbackPath}
        state={{ from: location }}
        replace
      />
    );
  }

  if (requiredChecks.length > 0) {
    const hasRequiredChecks = requiredChecks.some(check => resolveCheck(check));

    if (!hasRequiredChecks) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
